/**
 * 账本流水详情抽屉（ADM-24，entitlement-ledger-queries:SC-04/SC-05/SC-14）。
 *
 * 展示课时类别快照、可用/冻结两池前后值、业务来源与幂等键；
 * REVERSAL 流水额外展示冲正业务原因与被冲正原流水号（可再点开形成更正链）；
 * MANUAL_GRANT 流水额外内嵌赠课记录明细区（entitlement-core:SC-35 下钻）。
 * 只读视图：不提供任何修改余额或删除流水的操作。
 */
import { Descriptions, Drawer, Typography } from 'antd';
import dayjs from 'dayjs';
import type {
  LedgerAccountStatus,
  LedgerBusinessRefType,
  LedgerEntryDetail,
  LessonKind,
} from '../../api/entitlementLedger.api';
import { periodLedgerSummary } from '../../utils/period-ledger';
import {
  ManualTrialGrantDetailSection,
  useManualTrialGrantDetail,
} from './ManualTrialGrantDetailSection';

const { Text } = Typography;

/** 变动事由标签（O-14 起含期限卡激活/调整/使用三值，类型筛选下拉同源）。 */
export const ledgerTypeLabel: Record<string, string> = {
  GIFT: '赠送',
  PURCHASE: '购买开通',
  DEDUCT: '消课扣减',
  EXPIRE: '试课作废',
  FREEZE: '冻结',
  UNFREEZE: '解冻',
  REVERSAL: '冲正',
  ACTIVATE: '期限卡激活',
  ADJUST: '期限卡调整',
  USAGE: '期限卡使用',
};

/** 课时类别标签（单账户双池；O-14 起 PERIOD=期限卡事件行，不扣课时）。 */
export const lessonKindLabel: Record<LessonKind, string> = {
  FORMAL: '正式课',
  GIFTED: '赠送课',
  PERIOD: '期限卡事件（不扣课时）',
};

export const ledgerStatusTag: Record<LedgerAccountStatus, { text: string; color: string }> = {
  NORMAL: { text: '正常', color: 'green' },
  EXPIRED_VOID: { text: '试课已作废', color: 'default' },
  ZERO: { text: '余额为零', color: 'orange' },
};

export const ledgerBusinessRefLabel: Record<LedgerBusinessRefType, string> = {
  ENROLLMENT: '报名',
  ORDER: '订单',
  DEDUCTION: '消课',
  REVERSAL: '冲正',
  MANUAL_GRANT: '手动赠送',
  PERIOD_PASS: '期限卡',
};

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
}

export interface LedgerDetailDrawerProps {
  detail: LedgerEntryDetail | null;
  onClose: () => void;
}

export function LedgerDetailDrawer({ detail, onClose }: LedgerDetailDrawerProps) {
  // MANUAL_GRANT 流水：以 businessRefId 定位赠课记录，内嵌明细区（SC-35）
  const grantRecordId =
    detail?.businessRefType === 'MANUAL_GRANT' ? detail.businessRefId : null;
  const grantRecord = useManualTrialGrantDetail(grantRecordId);

  return (
    <Drawer
      title="流水详情"
      open={Boolean(detail)}
      onClose={onClose}
      width={560}
      destroyOnHidden
    >
      {detail && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="流水号">{detail.id}</Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {formatDateTime(detail.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="学生">
            {detail.studentName}（{detail.studentNo}）
          </Descriptions.Item>
          <Descriptions.Item label="账户">
            {detail.skuName}（{ledgerStatusTag[detail.accountStatus].text}）
          </Descriptions.Item>
          <Descriptions.Item label="类型">
            {ledgerTypeLabel[detail.type] ?? detail.type}
          </Descriptions.Item>
          <Descriptions.Item label="课时类别">
            {lessonKindLabel[detail.lessonKind] ?? detail.lessonKind}
          </Descriptions.Item>
          {detail.lessonKind === 'PERIOD' ? (
            // O-14：PERIOD 行以「期限卡事件」单项替代三组数量快照（含凭证回指与期限天数）
            <>
              <Descriptions.Item label="期限卡事件" data-testid="ledger-period-summary">
                {periodLedgerSummary(detail)}
              </Descriptions.Item>
              <Descriptions.Item label="期限天数">
                {detail.periodDays ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="凭证回指">
                {detail.periodPassId ?? '-'}
              </Descriptions.Item>
              {detail.reason && (
                <Descriptions.Item label="业务原因">{detail.reason}</Descriptions.Item>
              )}
            </>
          ) : (
            <>
              <Descriptions.Item label="正式课可用">
                {detail.formalBefore} → {detail.formalAfter}
                {detail.lessonKind === "FORMAL" ? "（本笔）" : ""}
              </Descriptions.Item>
              <Descriptions.Item label="赠送课可用">
                {detail.giftedBefore} → {detail.giftedAfter}
                {detail.lessonKind === "GIFTED" ? "（本笔）" : ""}
              </Descriptions.Item>
              <Descriptions.Item label="冻结变动（所属池）">
                {detail.frozenChange}（{detail.frozenBefore} → {detail.frozenAfter}）
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="业务来源">
            {ledgerBusinessRefLabel[detail.businessRefType] ?? detail.businessRefType}
          </Descriptions.Item>
          <Descriptions.Item label="业务编号">{detail.businessRefId}</Descriptions.Item>
          <Descriptions.Item label="操作人">{detail.operatorName ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="幂等键">
            {detail.idempotencyKey ?? <Text type="secondary">（空）</Text>}
          </Descriptions.Item>
          {detail.type === 'REVERSAL' && (
            <>
              <Descriptions.Item label="冲正业务原因">
                {detail.reason ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="被冲正原流水号">
                {detail.businessRefId}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      )}
      {grantRecordId && (
        <>
          <Typography.Title level={5} style={{ marginTop: 16 }}>
            赠课记录明细
          </Typography.Title>
          <ManualTrialGrantDetailSection {...grantRecord} />
        </>
      )}
    </Drawer>
  );
}
